import * as React from 'react';
import { Alert, Button, Form, Spinner, Stack } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'supertokens-auth';
import { AuthShell } from './AuthShell';

export const WebsitePasswordResetPage = (): React.JSX.Element => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasToken = Boolean(searchParams.get('token'));
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const requestReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const success = await auth.sendPasswordResetEmail(email.trim());
      if (success) setCooldown(60);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updatePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (password.length < 12) {
      setError('Password must be at least 12 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await auth.submitNewPassword(password);
      if (!success) {
        setError('This reset link is invalid, expired, or has already been used.');
        return;
      }
      window.setTimeout(() => navigate('/auth/login', { replace: true }), 1500);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      introduction={
        hasToken
          ? 'Choose a new password for your account.'
          : 'Enter your verified email address and we will send you a password reset link.'
      }
    >
      {auth.message && (
        <Alert variant={auth.message.type === 'error' ? 'danger' : 'info'}>{auth.message.text}</Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {hasToken ? (
        <Form onSubmit={updatePassword}>
          <Stack gap={3}>
            <Form.Group controlId="reset-password">
              <Form.Label>New password</Form.Label>
              <Form.Control
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <Form.Text>Use at least 12 characters.</Form.Text>
            </Form.Group>
            <Form.Group controlId="reset-password-confirmation">
              <Form.Label>Confirm new password</Form.Label>
              <Form.Control
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </Form.Group>
            <Button className="align-self-start w-auto" type="submit" disabled={isSubmitting}>
              {isSubmitting && <Spinner className="me-2" animation="border" size="sm" />}
              Update password
            </Button>
          </Stack>
        </Form>
      ) : (
        <Form onSubmit={requestReset}>
          <Stack gap={3}>
            <Form.Group controlId="reset-email">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Form.Group>
            <Button
              className="align-self-start w-auto"
              type="submit"
              disabled={isSubmitting || cooldown > 0}
            >
              {isSubmitting && <Spinner className="me-2" animation="border" size="sm" />}
              {cooldown > 0 ? `Send again in ${cooldown}s` : 'Send password reset email'}
            </Button>
          </Stack>
        </Form>
      )}
      <p className="mt-4 mb-0">
        <Link to="/auth/login">Return to login</Link>
      </p>
    </AuthShell>
  );
};
